const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

document.documentElement.classList.add("has-motion");

let revealObserver;

const prepareReveal = (element) => {
  element.classList.add("reveal");

  if (prefersReducedMotion) {
    element.classList.add("in-view");
  } else if (revealObserver) {
    revealObserver.observe(element);
  }
};

const revealTargets = [
  ".page-hero-inner",
  ".section-heading",
  ".archive-filters",
  ".product-layout",
  ".editorial-card",
  ".instagram-card",
  ".instagram-widget-shell",
  ".archive-chat-window",
  ".product-card",
  ".split-section",
  ".stat-card",
  ".value-card",
  ".press-card",
  ".timeline-card",
].flatMap((selector) => [...document.querySelectorAll(selector)]);

if (prefersReducedMotion) {
  revealTargets.forEach(prepareReveal);
} else {
  revealObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("in-view");
          revealObserver.unobserve(entry.target);
        }
      });
    },
    {
      rootMargin: "0px 0px -10% 0px",
      threshold: 0.14,
    }
  );

  revealTargets.forEach(prepareReveal);
}

const progress = document.querySelector(".scroll-progress");

if (progress) {
  const updateProgress = () => {
    const scrollable = document.documentElement.scrollHeight - window.innerHeight;
    const amount = scrollable > 0 ? window.scrollY / scrollable : 0;
    progress.style.transform = `scaleX(${Math.min(amount, 1)})`;
  };

  updateProgress();
  window.addEventListener("scroll", updateProgress, { passive: true });
  window.addEventListener("resize", updateProgress);
}

const archiveRoomGate = document.querySelector("[data-archive-room-gate]");

if (archiveRoomGate) {
  const archiveRoomWindow = archiveRoomGate.closest(".archive-chat-window");
  const archiveRoomSection = archiveRoomGate.closest(".archive-chatroom");
  const archiveRoomName = archiveRoomGate.querySelector("[data-archive-room-name]");
  const archiveRoomCode = archiveRoomGate.querySelector("[data-archive-room-code]");
  const archiveRoomError = archiveRoomGate.querySelector("[data-archive-room-error]");
  const archiveRoomStatus = document.querySelector("[data-archive-room-status]");
  const archiveRoomAccess = document.querySelector("[data-archive-room-access]");
  const archiveRoomComposer = document.querySelector("[data-archive-room-composer]");
  const archiveRoomMessageInput = document.querySelector("[data-archive-room-message]");
  const archiveRoomMessages = document.querySelector("[data-archive-room-messages]");
  const archiveRoomEmptyState = document.querySelector("[data-archive-room-empty-state]");
  const archiveRoomMembers = document.querySelector("[data-archive-room-members]");
  const archiveRoomMembersToggle = document.querySelector("[data-archive-room-members-toggle]");
  const archiveRoomMembersPanel = document.querySelector("[data-archive-room-members-panel]");
  const archiveRoomMemberAvatars = document.querySelector("[data-archive-room-member-avatars]");
  const archiveRoomMembersTitle = document.querySelector("[data-archive-room-members-title]");
  const archiveRoomMembersCount = document.querySelector("[data-archive-room-members-count]");
  const archiveRoomMembersList = document.querySelector("[data-archive-room-members-list]");
  const archiveRoomConfig = window.RSA_ABLY_ARCHIVE_ROOM || {};
  const archiveRoomFocusInputs = [archiveRoomName, archiveRoomCode, archiveRoomMessageInput].filter(Boolean);
  let archiveRoomChannel;
  let archiveRoomClient;
  let archiveRoomClientId = "";
  let archiveRoomScreenName = "";
  const archiveRoomSeenMessages = new Set();
  const archiveRoomMemberMap = new Map();

  const setArchiveRoomStatus = (status) => {
    if (archiveRoomStatus) {
      archiveRoomStatus.textContent = status;
    }
  };

  const cleanArchiveText = (value) => value.replace(/\s+/g, " ").trim().slice(0, 240);

  const archiveRoomInitials = (name) => {
    const words = cleanArchiveText(name || "Guest").split(" ").filter(Boolean);
    return words
      .slice(0, 2)
      .map((word) => word[0])
      .join("")
      .toUpperCase() || "G";
  };

  const renderArchiveRoomMembers = () => {
    if (!archiveRoomMembers || !archiveRoomMemberAvatars || !archiveRoomMembersCount || !archiveRoomMembersList) {
      return;
    }

    const members = [...archiveRoomMemberMap.values()]
      .map((name) => cleanArchiveText(name))
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));
    const count = members.length;

    archiveRoomMembers.hidden = false;
    archiveRoomMembersCount.textContent = count === 1 ? "1 inside" : `${count} inside`;

    if (archiveRoomMembersTitle) {
      archiveRoomMembersTitle.textContent = count > 1 ? "Archive Room" : members[0] || "Archive Room";
    }

    archiveRoomMemberAvatars.replaceChildren();
    members.slice(0, 4).forEach((name) => {
      const avatar = document.createElement("span");
      avatar.title = name;
      avatar.textContent = archiveRoomInitials(name);
      archiveRoomMemberAvatars.append(avatar);
    });

    if (count > 4) {
      const extra = document.createElement("span");
      extra.textContent = `+${count - 4}`;
      archiveRoomMemberAvatars.append(extra);
    }

    archiveRoomMembersList.replaceChildren();
    members.forEach((name) => {
      const item = document.createElement("li");
      const avatar = document.createElement("span");
      const label = document.createElement("strong");

      avatar.textContent = archiveRoomInitials(name);
      label.textContent = name;
      item.append(avatar, label);
      archiveRoomMembersList.append(item);
    });

    if (!members.length) {
      const item = document.createElement("li");
      const label = document.createElement("strong");

      item.className = "is-empty";
      label.textContent = "No one yet";
      item.append(label);
      archiveRoomMembersList.append(item);
    }
  };

  const setArchiveRoomMember = (id, name) => {
    const cleanName = cleanArchiveText(name || "Guest");

    if (!cleanName) {
      return;
    }

    archiveRoomMemberMap.set(id || cleanName.toLowerCase(), cleanName);
    renderArchiveRoomMembers();
  };

  const removeArchiveRoomMember = (id) => {
    archiveRoomMemberMap.delete(id);
    renderArchiveRoomMembers();
  };

  const syncArchiveRoomMembers = (members = []) => {
    archiveRoomMemberMap.clear();

    members.forEach((member) => {
      const name = cleanArchiveText(member.data?.name || member.clientId || "Guest");
      archiveRoomMemberMap.set(member.clientId || name.toLowerCase(), name);
    });

    renderArchiveRoomMembers();
  };

  archiveRoomMembersToggle?.addEventListener("click", () => {
    const isOpen = archiveRoomMembersToggle.getAttribute("aria-expanded") === "true";
    archiveRoomMembersToggle.setAttribute("aria-expanded", String(!isOpen));
    archiveRoomMembers?.classList.toggle("is-open", !isOpen);

    if (archiveRoomMembersPanel) {
      archiveRoomMembersPanel.hidden = isOpen;
    }
  });

  renderArchiveRoomMembers();

  const addArchiveRoomLine = (kind, text, name = "") => {
    if (!archiveRoomMessages || !text) {
      return;
    }

    const line = document.createElement("div");
    line.className = `archive-room-line ${kind === "message" ? "is-message" : "is-system"}`;

    if (kind === "message" && name) {
      const author = document.createElement("span");
      author.className = "archive-room-line-author";
      author.textContent = name;
      line.append(author);
    }

    const body = document.createElement("span");
    body.className = "archive-room-line-body";
    body.textContent = text;
    line.append(body);

    archiveRoomMessages.append(line);
    archiveRoomMessages.scrollTop = archiveRoomMessages.scrollHeight;
  };

  const echoArchiveRoomMessage = (payload) => {
    const id = payload.id || "";

    if (id) {
      if (archiveRoomSeenMessages.has(id)) {
        return;
      }

      archiveRoomSeenMessages.add(id);
    }

    addArchiveRoomLine(
      "message",
      cleanArchiveText(payload.text || ""),
      cleanArchiveText(payload.name || "Guest"),
    );
  };

  const publishArchiveRoomMessage = (payload) => {
    const canPublish = archiveRoomChannel && archiveRoomClient?.connection?.state === "connected";

    echoArchiveRoomMessage(payload);

    if (!canPublish) {
      return;
    }

    try {
      const result = archiveRoomChannel.publish("message", payload, () => {});

      if (result && typeof result.catch === "function") {
        result.catch(() => {});
      }
    } catch (error) {
      setArchiveRoomStatus("Offline");
    }
  };

  const isArchiveRoomMobile = () => window.matchMedia("(max-width: 700px)").matches;

  const setArchiveRoomAppViewport = () => {
    const viewportHeight = window.visualViewport?.height || window.innerHeight;
    const viewportTop = window.visualViewport?.offsetTop || 0;

    document.documentElement.style.setProperty("--archive-room-app-top", `${Math.round(viewportTop)}px`);
    document.documentElement.style.setProperty("--archive-room-app-height", `${Math.round(viewportHeight)}px`);
  };

  const syncArchiveRoomAppMode = () => {
    if (!archiveRoomWindow || !archiveRoomSection) {
      return;
    }

    if (!isArchiveRoomMobile()) {
      document.documentElement.classList.remove("archive-room-app-open");
      document.body.classList.remove("archive-room-app-open");
      archiveRoomSection.classList.remove("is-keyboard-active", "is-mobile-app");
      archiveRoomWindow.classList.remove("is-input-focused");
      document.documentElement.style.removeProperty("--archive-room-app-top");
      document.documentElement.style.removeProperty("--archive-room-app-height");
      return;
    }

    setArchiveRoomAppViewport();
    document.documentElement.classList.add("archive-room-app-open");
    document.body.classList.add("archive-room-app-open");
    archiveRoomSection.classList.add("is-keyboard-active", "is-mobile-app");

    if (archiveRoomFocusInputs.includes(document.activeElement)) {
      archiveRoomWindow.classList.add("is-input-focused");
    } else {
      archiveRoomWindow.classList.remove("is-input-focused");
    }

    if (archiveRoomMessages) {
      archiveRoomMessages.scrollTop = archiveRoomMessages.scrollHeight;
    }
  };

  syncArchiveRoomAppMode();

  const syncArchiveRoomAppModeSoon = () => {
    syncArchiveRoomAppMode();
    window.setTimeout(syncArchiveRoomAppMode, 120);
    window.setTimeout(syncArchiveRoomAppMode, 320);
  };

  archiveRoomFocusInputs.forEach((input) => {
    input.addEventListener("focus", syncArchiveRoomAppModeSoon);
    input.addEventListener("input", syncArchiveRoomAppMode);
    input.addEventListener("blur", () => {
      window.setTimeout(syncArchiveRoomAppMode, 120);
    });
  });

  window.addEventListener("resize", syncArchiveRoomAppMode);
  window.addEventListener("orientationchange", () => {
    window.setTimeout(syncArchiveRoomAppMode, 240);
  });

  if (window.visualViewport) {
    window.visualViewport.addEventListener("resize", syncArchiveRoomAppMode);
    window.visualViewport.addEventListener("scroll", syncArchiveRoomAppMode);
  }

  const updateArchiveRoomPresence = async () => {
    if (!archiveRoomChannel) {
      return;
    }

    try {
      const members = await archiveRoomChannel.presence.get();
      const count = members.length;

      syncArchiveRoomMembers(members);
      setArchiveRoomStatus(`${count} inside`);
    } catch (error) {
      setArchiveRoomStatus("Room open");
    }
  };

  const connectArchiveRoom = async () => {
    if (archiveRoomChannel) {
      return;
    }

    if (!window.Ably) {
      setArchiveRoomStatus("Needs Ably");
      addArchiveRoomLine("system", "Live chat is waiting for the Ably connection.");
      return;
    }

    const channelName = archiveRoomConfig.channel || "rsa:archive-room";
    const ablyOptions = {
      clientId: archiveRoomClientId || archiveRoomScreenName,
    };

    if (archiveRoomConfig.key) {
      ablyOptions.key = archiveRoomConfig.key;
    } else {
      ablyOptions.authUrl = archiveRoomConfig.authUrl || "/apps/rsa-ably-token";
      ablyOptions.authParams = {
        room: "archive-room",
      };
    }

    setArchiveRoomStatus("Connecting");

    try {
      archiveRoomClient = new window.Ably.Realtime(ablyOptions);

      archiveRoomChannel = archiveRoomClient.channels.get(channelName);

      archiveRoomClient.connection.on("connected", () => {
        setArchiveRoomStatus("Live");
      });

      archiveRoomClient.connection.on("failed", () => {
        setArchiveRoomStatus("Offline");
        addArchiveRoomLine("system", "Live chat needs the RSA Ably token endpoint connected.");
      });

      archiveRoomChannel.subscribe("message", (message) => {
        const data = message.data || {};
        echoArchiveRoomMessage({
          id: data.id || message.id || "",
          name: data.name || "Guest",
          text: data.text || "",
        });
      });

      archiveRoomChannel.presence.subscribe(["enter", "leave"], (member) => {
        const name = cleanArchiveText(member.data?.name || member.clientId || "Someone");

        if (member.action === "enter") {
          setArchiveRoomMember(member.clientId || name.toLowerCase(), name);
        } else {
          removeArchiveRoomMember(member.clientId || name.toLowerCase());
        }

        addArchiveRoomLine("system", `${name} ${member.action === "enter" ? "entered" : "left"} the room.`);
        updateArchiveRoomPresence();
      });

      await archiveRoomChannel.presence.enter({ name: archiveRoomScreenName });
      updateArchiveRoomPresence();

      window.addEventListener("beforeunload", () => {
        archiveRoomChannel?.presence.leave();
      });
    } catch (error) {
      setArchiveRoomStatus("Offline");
      addArchiveRoomLine("system", "Live chat needs the RSA Ably token endpoint connected.");
    }
  };

  archiveRoomGate.addEventListener("submit", (event) => {
    event.preventDefault();

    if (archiveRoomCode?.value.trim() === "318") {
      archiveRoomScreenName = cleanArchiveText(archiveRoomName?.value || "Guest");
      archiveRoomClientId = `${archiveRoomScreenName.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${Date.now().toString(36)}`;
      archiveRoomWindow?.classList.add("is-unlocked");
      archiveRoomSection?.classList.add("is-room-unlocked");
      setArchiveRoomMember(archiveRoomClientId, archiveRoomScreenName);

      setArchiveRoomStatus("Room open");

      if (archiveRoomAccess) {
        archiveRoomAccess.textContent = "Access granted";
      }

      if (archiveRoomEmptyState) {
        archiveRoomEmptyState.hidden = true;
      }

      if (archiveRoomComposer) {
        archiveRoomComposer.hidden = false;
      }

      addArchiveRoomLine("system", `${archiveRoomScreenName} entered the room.`);
      connectArchiveRoom();

      return;
    }

    if (archiveRoomError) {
      archiveRoomError.hidden = false;
    }
  });

  archiveRoomComposer?.addEventListener("submit", (event) => {
    event.preventDefault();

    const text = cleanArchiveText(archiveRoomMessageInput?.value || "");

    if (!text) {
      return;
    }

    publishArchiveRoomMessage({
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      name: archiveRoomScreenName || "Guest",
      text,
      sentAt: Date.now(),
    });

    archiveRoomMessageInput.value = "";
    archiveRoomMessageInput.focus();
  });
}

const smoothAnchorHashes = new Set(["#product", "#inside"]);

const easeOutCubic = (progress) => 1 - Math.pow(1 - progress, 3);
const normalizedPath = (path) => path.replace(/\/$/, "");
const pendingAnchorStorageKey = "rsaPendingAnchorHash";

const readPendingAnchorHash = () => {
  try {
    const pendingHash = window.sessionStorage?.getItem(pendingAnchorStorageKey);

    if (pendingHash) {
      window.sessionStorage.removeItem(pendingAnchorStorageKey);
    }

    return pendingHash;
  } catch (error) {
    return "";
  }
};

const savePendingAnchorHash = (hash) => {
  try {
    window.sessionStorage?.setItem(pendingAnchorStorageKey, hash);
  } catch (error) {
    // Session storage can be unavailable in some private mobile browsers.
  }
};

const scrollToAnchorTarget = (hash, updateHash = true) => {
  const target = smoothAnchorHashes.has(hash) ? document.querySelector(hash) : null;

  if (!target) {
    return false;
  }

  const targetStyles = window.getComputedStyle(target);
  const scrollMarginTop = Number.parseFloat(targetStyles.scrollMarginTop) || 0;
  const startY = window.scrollY;
  const targetY = Math.max(0, target.getBoundingClientRect().top + startY - scrollMarginTop);
  const distance = targetY - startY;
  const duration = Math.min(980, Math.max(520, Math.abs(distance) * 0.58));

  const startTime = window.performance.now();

  const step = (now) => {
    const progress = Math.min((now - startTime) / duration, 1);
    window.scrollTo(0, startY + distance * easeOutCubic(progress));

    if (progress < 1) {
      window.requestAnimationFrame(step);
    }
  };

  window.requestAnimationFrame(step);

  if (updateHash && window.history?.pushState) {
    window.setTimeout(() => {
      window.history.pushState(null, "", hash);
    }, duration);
  }

  return true;
};

let lastAnchorTouchTime = 0;

const handleAnchorLink = (event) => {
  const link = event.target instanceof Element ? event.target.closest("a[href]") : null;

  if (!link) {
    return;
  }

  const url = new URL(link.href, window.location.href);
  const currentPath = normalizedPath(window.location.pathname);
  const linkPath = normalizedPath(url.pathname);
  const isSameOrigin = url.origin === window.location.origin;
  const isSamePage = isSameOrigin && currentPath === linkPath;
  const isHomeHashLink = isSameOrigin && smoothAnchorHashes.has(url.hash) && (linkPath === "" || linkPath === currentPath);
  const hasAnchorTarget = smoothAnchorHashes.has(url.hash) && document.querySelector(url.hash);

  if (!isHomeHashLink && !hasAnchorTarget) {
    return;
  }

  if (event.type === "click" && Date.now() - lastAnchorTouchTime < 900) {
    event.preventDefault();
    return;
  }

  if (event.type === "touchend") {
    lastAnchorTouchTime = Date.now();
  }

  if (isSamePage && hasAnchorTarget) {
    event.preventDefault();
    scrollToAnchorTarget(url.hash);
    return;
  }

  if (isHomeHashLink) {
    if (event.cancelable !== false) {
      event.preventDefault();
    }

    savePendingAnchorHash(url.hash);
    window.location.assign(`${url.origin}${url.pathname}${url.search}`);
  }
};

document.addEventListener("touchend", handleAnchorLink, { capture: true, passive: false });
document.addEventListener("click", handleAnchorLink, { capture: true });

const initialAnchorHash = readPendingAnchorHash() || window.location.hash;

if (smoothAnchorHashes.has(initialAnchorHash)) {
  if ("scrollRestoration" in window.history) {
    window.history.scrollRestoration = "manual";
  }

  window.setTimeout(() => {
    const target = document.querySelector(initialAnchorHash);

    if (!target) {
      return;
    }

    const targetStyles = window.getComputedStyle(target);
    const scrollMarginTop = Number.parseFloat(targetStyles.scrollMarginTop) || 0;
    const targetY = Math.max(0, target.getBoundingClientRect().top + window.scrollY - scrollMarginTop);
    const alreadyAtTarget = Math.abs(window.scrollY - targetY) < 10;

    if (alreadyAtTarget && window.scrollY > 10) {
      window.scrollTo(0, 0);
      window.setTimeout(() => scrollToAnchorTarget(initialAnchorHash, false), 120);
    } else {
      scrollToAnchorTarget(initialAnchorHash, false);
    }
  }, 180);
}

const cursorGlow = document.querySelector(".cursor-glow");

if (cursorGlow && !prefersReducedMotion) {
  window.addEventListener(
    "pointermove",
    (event) => {
      cursorGlow.style.transform = `translate3d(${event.clientX}px, ${event.clientY}px, 0) translate(-50%, -50%)`;
    },
    { passive: true }
  );
}

const motionPanels = [...document.querySelectorAll(".hero, .page-hero")];

motionPanels.forEach((panel) => {
  if (prefersReducedMotion) {
    return;
  }

  panel.addEventListener("pointermove", (event) => {
    const bounds = panel.getBoundingClientRect();
    const x = (event.clientX - bounds.left) / bounds.width - 0.5;
    const y = (event.clientY - bounds.top) / bounds.height - 0.5;
    panel.style.setProperty("--mx", x.toFixed(3));
    panel.style.setProperty("--my", y.toFixed(3));
  });

  panel.addEventListener("pointerleave", () => {
    panel.style.setProperty("--mx", "0");
    panel.style.setProperty("--my", "0");
  });
});

const filterButtons = [...document.querySelectorAll("[data-filter]")];
const getFilterCards = () => [...document.querySelectorAll(".shop-grid .product-card")];
const filterNames = new Set(filterButtons.map((button) => button.dataset.filter));
const shopSearchInput = document.querySelector("[data-shop-search]");
const shopSearchClear = document.querySelector("[data-shop-search-clear]");
const shopSearchStatus = document.querySelector("[data-shop-search-status]");
const shopSearchGrid = shopSearchInput?.closest(".archive-section")?.querySelector(".shop-grid");
const originalShopSearchGridHtml = shopSearchGrid?.innerHTML || "";
let currentProductFilter = "all";
let shopSearchRequestId = 0;
let shopSearchTimer;
const curatedShopSearchProducts = [
  {
    handle: "xxl-magazine-november-2005-lil-wayne-cover",
    terms: ["hurricane", "katrina", "hurricane katrina", "new orleans", "soul survivor"],
  },
];

const normalizeSearchText = (value) => (value || "").toLowerCase().replace(/\s+/g, " ").trim();
const moneyFromCents = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format((Number(value) || 0) / 100);

const applyProductFilter = (filter, updateUrl = false) => {
  if (!filterNames.has(filter)) {
    filter = "all";
  }

  currentProductFilter = filter;
  const query = normalizeSearchText(shopSearchInput?.value || "");
  let visibleCount = 0;

  filterButtons.forEach((item) => item.classList.toggle("is-active", item.dataset.filter === filter));
  getFilterCards().forEach((card) => {
    const cardType = card.dataset.type || "all";
    const filterMatch = filter === "all" || cardType === filter;
    const searchText = normalizeSearchText(`${card.textContent} ${cardType} ${card.dataset.search || ""}`);
    const searchMatch = !query || searchText.includes(query);
    const isVisible = filterMatch && searchMatch;
    card.classList.toggle("is-hidden", !isVisible);

    if (isVisible) {
      visibleCount += 1;
    }
  });

  if (shopSearchClear) {
    shopSearchClear.hidden = !query;
  }

  if (shopSearchStatus) {
    shopSearchStatus.textContent = query
      ? `${visibleCount} ${visibleCount === 1 ? "drop" : "drops"} found`
      : "";
  }

  if (updateUrl && window.history?.pushState) {
    const url = new URL(window.location.href);

    if (filter === "all") {
      url.searchParams.delete("filter");
    } else {
      url.searchParams.set("filter", filter);
    }

    window.history.pushState({ filter }, "", url);
  }
};

const refreshShopSearchCards = () => {
  getFilterCards().forEach((card) => {
    prepareReveal(card);
    enableCardLift(card);
  });
};

const restoreLocalShopSearchGrid = () => {
  if (!shopSearchGrid || !originalShopSearchGridHtml) {
    return;
  }

  shopSearchGrid.innerHTML = originalShopSearchGridHtml;
  refreshShopSearchCards();
};

const productMatchesCuratedSearch = (query, item) =>
  item.terms.some((term) => normalizeSearchText(query).includes(term));

const cardFromProductData = (product) => {
  if (!product?.title || !product?.url) {
    return null;
  }

  const card = document.createElement("article");
  const imageLink = document.createElement("a");
  const image = document.createElement("img");
  const copy = document.createElement("div");
  const label = document.createElement("p");
  const title = document.createElement("h3");
  const type = document.createElement("span");
  const footer = document.createElement("footer");
  const price = document.createElement("span");
  const cta = document.createElement("a");

  card.className = `product-card${product.available ? "" : " is-sold-out"}`;
  card.dataset.type = product.type ? product.type.toLowerCase().replace(/\s+/g, "-") : "all";
  card.dataset.search = normalizeSearchText(`${product.title} ${product.vendor} ${product.type} ${product.description || ""}`);

  const productUrl = product.url || `/products/${product.handle}`;
  const productImage = product.featured_image || product.images?.[0] || "";

  imageLink.href = productUrl;
  image.src = productImage ? `${productImage}${productImage.includes("?") ? "&" : "?"}width=700` : "";
  image.alt = product.title;
  image.loading = "lazy";
  imageLink.append(image);

  label.className = "card-label";
  label.textContent = "Result";
  title.textContent = product.title;
  type.textContent = product.type || "XXL Magazine";

  footer.className = "shop-card-footer";
  cta.className = product.available
    ? "button primary shop-card-cta arrow-only"
    : "button primary shop-card-cta sold-out-cta";
  cta.href = productUrl;
  cta.setAttribute("aria-label", product.available ? `View ${product.title}` : `${product.title} is sold out`);
  cta.textContent = product.available ? "\u2192" : "Sold out";

  if (product.available) {
    price.className = "product-price";
    price.textContent = moneyFromCents(product.price);
    footer.append(price, cta);
  } else {
    footer.append(cta);
  }

  copy.append(label, title, type, footer);
  card.append(imageLink, copy);

  return card;
};

const curatedShopSearchCards = async (query) => {
  const matches = curatedShopSearchProducts.filter((item) => productMatchesCuratedSearch(query, item));

  if (!matches.length) {
    return [];
  }

  const cards = await Promise.all(
    matches.map(async (item) => {
      try {
        const response = await fetch(`/products/${item.handle}.js`);
        const product = await response.json();
        return cardFromProductData(product);
      } catch (error) {
        return null;
      }
    })
  );

  return cards.filter(Boolean);
};

const fetchShopSearchResults = async (query) => {
  if (!shopSearchGrid || query.length < 2) {
    return;
  }

  const requestId = ++shopSearchRequestId;
  const url = `/search?q=${encodeURIComponent(query)}&type=product`;

  if (shopSearchStatus) {
    shopSearchStatus.textContent = "Searching the archive...";
  }

  try {
    const response = await fetch(url, {
      headers: {
        "X-Requested-With": "XMLHttpRequest",
      },
    });
    const html = await response.text();
    const page = new DOMParser().parseFromString(html, "text/html");
    const resultGrid = page.querySelector(".shop-grid");
    const resultCards = resultGrid ? [...resultGrid.querySelectorAll(".product-card")] : [];
    const curatedCards = await curatedShopSearchCards(query);
    const currentQuery = normalizeSearchText(shopSearchInput?.value || "");

    if (requestId !== shopSearchRequestId || currentQuery !== normalizeSearchText(query)) {
      return;
    }

    if ((resultGrid && resultCards.length) || curatedCards.length) {
      shopSearchGrid.innerHTML = resultGrid ? resultGrid.innerHTML : "";
      const existingUrls = new Set(
        [...shopSearchGrid.querySelectorAll(".product-card a[href]")].map((link) => link.getAttribute("href"))
      );

      curatedCards.reverse().forEach((card) => {
        const url = card.querySelector("a[href]")?.getAttribute("href");

        if (!url || existingUrls.has(url)) {
          return;
        }

        shopSearchGrid.prepend(card);
        existingUrls.add(url);
      });

      const totalCards = shopSearchGrid.querySelectorAll(".product-card").length;
      refreshShopSearchCards();
      if (shopSearchStatus) {
        shopSearchStatus.textContent = `${totalCards} ${totalCards === 1 ? "drop" : "drops"} found`;
      }
    } else {
      shopSearchGrid.innerHTML = '<p class="archive-search-empty">No drops found yet.</p>';
      if (shopSearchStatus) {
        shopSearchStatus.textContent = "0 drops found";
      }
    }
  } catch (error) {
    applyProductFilter(currentProductFilter);
  }
};

const queueShopSearchResults = () => {
  const query = normalizeSearchText(shopSearchInput?.value || "");

  window.clearTimeout(shopSearchTimer);

  if (!query) {
    shopSearchRequestId += 1;
    restoreLocalShopSearchGrid();
    applyProductFilter(currentProductFilter);
    return;
  }

  shopSearchTimer = window.setTimeout(() => {
    fetchShopSearchResults(query);
  }, 220);
};

filterButtons.forEach((button) => {
  button.addEventListener("click", (event) => {
    if (button.tagName === "A") {
      event.preventDefault();
    }

    applyProductFilter(button.dataset.filter, true);
  });
});

shopSearchInput?.addEventListener("input", () => {
  applyProductFilter(currentProductFilter);
  queueShopSearchResults();
});

shopSearchClear?.addEventListener("click", () => {
  shopSearchInput.value = "";
  shopSearchInput.focus();
  restoreLocalShopSearchGrid();
  applyProductFilter(currentProductFilter);
});

if (filterButtons.length) {
  const initialFilter = new URLSearchParams(window.location.search).get("filter");
  applyProductFilter(initialFilter || "all");
} else if (shopSearchInput) {
  applyProductFilter("all");
}

const enableCardLift = (card) => {
  if (prefersReducedMotion) {
    return;
  }

  card.addEventListener("pointermove", (event) => {
    const bounds = card.getBoundingClientRect();
    const x = (event.clientX - bounds.left) / bounds.width - 0.5;
    const y = (event.clientY - bounds.top) / bounds.height - 0.5;
    card.style.transform = `translate(${x * -6}px, ${y * -6}px)`;
  });

  card.addEventListener("pointerleave", () => {
    card.style.transform = "";
  });
};

const liftCards = [...document.querySelectorAll(".editorial-card, .instagram-card, .product-card, .press-card, .value-card")];

liftCards.forEach(enableCardLift);

const instagramCards = document.querySelector("[data-instagram-cards]");
const instagramWidgetShell = document.querySelector(".instagram-widget-shell");
const freeWidgetText = "free instagram feed widget";
const pinnedInstagramPostCount = Math.max(
  0,
  Number.parseInt(instagramWidgetShell?.dataset?.skipPinnedPosts || "3", 10) || 0
);

const escapeHtml = (value = "") =>
  value
    .toString()
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");

const compactText = (value = "") => value.replace(/\s+/g, " ").trim();

const directText = (node) =>
  compactText(
    [...node.childNodes]
      .filter((child) => child.nodeType === Node.TEXT_NODE)
      .map((child) => child.textContent)
      .join(" ")
  );

const titleFromCaption = (caption = "") => {
  const cleanCaption = compactText(caption);
  return cleanCaption ? cleanCaption.slice(0, 86) : "Latest Instagram post";
};

const cleanImageUrl = (url = "") => url.replace(/&amp;/g, "&").trim();

const imageUrlFromStyle = (node) =>
  cleanImageUrl(node?.style?.backgroundImage?.match(/url\(["']?(.*?)["']?\)/)?.[1] || "");

const imageScore = (image) => {
  const source = cleanImageUrl(image.currentSrc || image.src || image.dataset?.src || image.getAttribute("src") || "");
  const className = `${image.className || ""} ${image.parentElement?.className || ""}`.toLowerCase();
  const area =
    (image.naturalWidth || Number.parseInt(image.getAttribute("width"), 10) || 0) *
    (image.naturalHeight || Number.parseInt(image.getAttribute("height"), 10) || 0);

  let score = area || 1;

  if (/post|media|image|thumbnail/.test(className)) score += 500000;
  if (/avatar|profile|logo|icon/.test(className)) score -= 750000;
  if (/data:image|svg|spacer|blank|transparent/i.test(source)) score -= 1000000;

  return score;
};

const imageFromElfsightPost = (post) => {
  const backgroundNode = [...post.querySelectorAll("[style*='background-image']")]
    .map((node) => ({
      node,
      url: imageUrlFromStyle(node),
      score: /post|media|image|thumbnail/i.test(`${node.className || ""}`) ? 600000 : 1,
    }))
    .filter((candidate) => candidate.url && !/data:image|svg|spacer|blank|transparent/i.test(candidate.url))
    .sort((a, b) => b.score - a.score)[0];

  if (backgroundNode?.url) {
    return backgroundNode.url;
  }

  const image = [...post.querySelectorAll("img")]
    .map((node) => ({
      node,
      url: cleanImageUrl(node.currentSrc || node.src || node.dataset?.src || node.getAttribute("src") || ""),
      score: imageScore(node),
    }))
    .filter((candidate) => candidate.url && candidate.score > -500000)
    .sort((a, b) => b.score - a.score)[0];

  return image?.url || "";
};

const instagramUrlFromPost = (post) => {
  if (post.matches?.("a[href*='instagram.com']")) {
    return post.href;
  }

  return (
    post.querySelector("a[href*='instagram.com/p/'], a[href*='instagram.com/reel/'], a[href*='instagram.com']")?.href ||
    "https://www.instagram.com/rapstylearcheology"
  );
};

const isLikelyElfsightPostCard = (node) => {
  const className = `${node.className || ""}`.toLowerCase();

  return (
    className.includes("eapps-instagram-feed-posts-item") &&
    !/image|text|caption|overlay|user|avatar|icon|likes|comments|share|date|link/.test(className)
  );
};

const postRootFromNode = (node) => {
  let current = node;

  while (current && current !== instagramWidgetShell) {
    if (isLikelyElfsightPostCard(current)) {
      return current;
    }

    current = current.parentElement;
  }

  return node.closest("article") || node;
};

const uniqueBy = (items, key) => {
  const seen = new Set();

  return items.filter((item) => {
    const value = key(item);

    if (!value || seen.has(value)) {
      return false;
    }

    seen.add(value);
    return true;
  });
};

const instagramPostKey = (post) => {
  const url = instagramUrlFromPost(post);

  if (url.includes("/p/") || url.includes("/reel/")) {
    return url;
  }

  return imageFromElfsightPost(post) || compactText(post.textContent).slice(0, 120);
};

const getElfsightPostNodes = () => {
  if (!instagramWidgetShell) {
    return [];
  }

  const primaryPosts = uniqueBy(
    [...instagramWidgetShell.querySelectorAll(".eapps-instagram-feed-posts-item")].filter(isLikelyElfsightPostCard),
    instagramPostKey
  );

  if (primaryPosts.length) {
    return primaryPosts;
  }

  const postLinks = [
    ...instagramWidgetShell.querySelectorAll("a[href*='instagram.com/p/'], a[href*='instagram.com/reel/']"),
  ];

  return uniqueBy(postLinks.map(postRootFromNode), instagramPostKey);
};

const hideFreeWidgetLabels = () => {
  const possibleLabels = [...document.querySelectorAll("a, div, span")].filter((node) =>
    directText(node).toLowerCase().includes(freeWidgetText)
  );

  possibleLabels.forEach((node) => {
    node.style.setProperty("display", "none", "important");
  });
};

const renderInstagramCards = (posts) => {
  if (!instagramCards || posts.length < 3) {
    return false;
  }

  const firstThreePosts = posts.slice(0, 3);

  instagramCards.hidden = false;
  instagramCards.classList.add("has-live-posts");
  instagramWidgetShell?.classList.add("is-data-source");

  instagramCards.innerHTML = firstThreePosts
    .map((post, index) => {
      const badge = index === 0 ? '<span class="instagram-badge">Latest post</span>' : "";
      const title = titleFromCaption(post.caption || post.alt);

      return `
        <article class="instagram-card">
          <a href="${escapeHtml(post.url)}" aria-label="View ${escapeHtml(title)} on Instagram">
            <div class="instagram-media">
              <img src="${escapeHtml(post.image)}" alt="${escapeHtml(title)}" />
              ${badge}
            </div>
            <div class="instagram-copy">
              <p class="card-label">@rapstylearcheology</p>
              <h3>${escapeHtml(title)}</h3>
              <span>View on Instagram</span>
            </div>
          </a>
        </article>
      `;
    })
    .join("");

  [...instagramCards.querySelectorAll(".instagram-card")].forEach((card) => {
    prepareReveal(card);
    enableCardLift(card);
  });

  return true;
};

const displayInstagramPosts = (posts) => {
  if (pinnedInstagramPostCount && posts.length > pinnedInstagramPostCount) {
    return posts.slice(pinnedInstagramPostCount, pinnedInstagramPostCount + 3);
  }

  return posts.slice(0, 3);
};

if (instagramWidgetShell) {
  let instagramSyncAttempts = 0;

  const syncElfsightPosts = () => {
    hideFreeWidgetLabels();

    const posts = getElfsightPostNodes();
    const selectedPosts = displayInstagramPosts(posts);
    const selectedKeys = new Set(selectedPosts.map(instagramPostKey));

    if (selectedPosts.length) {
      posts.forEach((post) => {
        post.hidden = !selectedKeys.has(instagramPostKey(post));
      });
    }

    const visiblePosts = selectedPosts
      .map((post) => ({
        url: instagramUrlFromPost(post),
        image: imageFromElfsightPost(post),
        caption: post.querySelector(".eapps-instagram-feed-posts-item-text")?.textContent || post.textContent,
        alt: post.querySelector("img")?.alt,
      }))
      .filter((post) => post.image);

    const hasStyledFeed = renderInstagramCards(visiblePosts);

    if (!hasStyledFeed) {
      instagramWidgetShell?.classList.remove("is-data-source");

      if (instagramCards) {
        instagramCards.hidden = true;
        instagramCards.classList.remove("has-live-posts");
        instagramCards.innerHTML = "";
      }
    }

    if (!visiblePosts.length && instagramSyncAttempts < 120) {
      instagramSyncAttempts += 1;
      window.setTimeout(syncElfsightPosts, 500);
    }
  };

  syncElfsightPosts();

  new MutationObserver(syncElfsightPosts).observe(instagramWidgetShell, {
    childList: true,
    subtree: true,
  });
}
